import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import PartnersSection from '../components/PartnersSection'
import { supabase } from '../lib/supabaseClient'
import '../home.css'

type Testimonial = {
  id: string
  reviewer_name: string
  rating: number
  comment: string | null
}

const FALLBACK_TESTIMONIALS: Testimonial[] = [
  { id: 'f1', reviewer_name: 'Marc', rating: 5, comment: "Service impeccable ! Ma raquette était prête en 24h et le cordage est parfait. Les conseils sont vraiment utiles." },
  { id: 'f2', reviewer_name: 'Sophie', rating: 5, comment: "Très professionnel, tension parfaitement réglée. Je recommande vivement pour le badminton." },
  { id: 'f3', reviewer_name: 'Jean', rating: 5, comment: "Excellent rapport qualité-prix. Service local et rapide, exactement ce qu'il me fallait." },
]

const Home: React.FC = () => {
  const navigate = useNavigate()
  const [testimonials, setTestimonials] = useState<Testimonial[]>(FALLBACK_TESTIMONIALS)

  useEffect(() => {
    supabase
      .from('order_feedbacks')
      .select('id, reviewer_name, rating, comment')
      .eq('is_visible', true)
      .not('comment', 'is', null)
      .order('created_at', { ascending: false })
      .limit(6)
      .then(({ data }) => {
        if (data && data.length > 0) setTestimonials(data as Testimonial[])
      })
  }, [])

  const handleBooking = () => {
    navigate('/order/start')
  }

  const handleContact = () => {
    window.open('https://t.me/stringnow', '_blank')
  }

  const handleContactWhatsApp = () => {
    window.open('https://wa.me/message/LMQEI75YYCOBA1', '_blank')
  }

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section" data-testid="home-section">
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title" data-testid="home-heading">
              Cordage professionnel de raquettes à Crissier et Lausanne
            </h1>
            <p className="hero-subtitle" data-testid="home-description">
              Tennis · Badminton · Squash — réglé selon ton jeu, prêt rapidement.
              Tarifs préférentiels pour clubs et centres sportifs partenaires.
            </p>
            
            <div className="hero-features">
              <div className="hero-feature">
                <svg className="hero-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Délai moyen 24h à 48h</span>
              </div>
              <div className="hero-feature">
                <svg className="hero-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                </svg>
                <span>Dépôt, retrait et livraison</span>
              </div>
              <div className="hero-feature">
                <svg className="hero-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Conseils personnalisés</span>
              </div>
            </div>

            <div className="hero-cta">
              <button 
                className="btn-hero-primary" 
                onClick={handleBooking}
                data-testid="home-cta-button"
              >
                Réserver maintenant
              </button>
              <button 
                className="btn-hero-secondary" 
                onClick={handleContactWhatsApp}
              >
                Besoin d'un conseil ?
              </button>
            </div>
          </div>
          
          <div className="hero-image">
            <img 
              src="/images/machine-cordage.png" 
              alt="Machine à corder professionnelle"
              className="hero-image-real"
            />
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="services-section">
        <h2 className="section-title">Nos services de cordage</h2>
        
        <div className="services-grid">
          <div className="service-card">
            <div className="service-icon">
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h3 className="service-title">Cordage Tennis</h3>
            <p className="service-description">
              Cordage précis adapté à votre style de jeu. Tension réglée selon vos besoins pour un contrôle optimal ou plus de puissance.
            </p>
          </div>

          <div className="service-card">
            <div className="service-icon">
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <h3 className="service-title">Cordage Badminton</h3>
            <p className="service-description">
              Montage soigné pour badminton. Réglage précis de la tension pour un équilibre parfait entre réactivité et durabilité.
            </p>
          </div>

          <div className="service-card">
            <div className="service-icon">
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
              </svg>
            </div>
            <h3 className="service-title">Cordage Squash</h3>
            <p className="service-description">
              Service spécialisé pour squash. Tension adaptée à l'intensité du jeu pour une résistance et une précision maximales.
            </p>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works-section">
        <h2 className="section-title">Comment ça marche</h2>
        
        <div className="steps-grid">
          <div className="step-card">
            <div className="step-number">1</div>
            <h3 className="step-title">Dépôt de la raquette</h3>
            <p className="step-description">
              Déposez votre raquette à notre atelier à Crissier ou demandez un enlèvement à Lausanne et alentours.
            </p>
          </div>

          <div className="step-card">
            <div className="step-number">2</div>
            <h3 className="step-title">Choix du cordage</h3>
            <p className="step-description">
              Sélectionnez votre cordage et tension, ou profitez de nos conseils personnalisés.
            </p>
          </div>

          <div className="step-card">
            <div className="step-number">3</div>
            <h3 className="step-title">Cordage professionnel</h3>
            <p className="step-description">
              Nous cordons votre raquette avec soin et précision sur machine professionnelle.
            </p>
          </div>

          <div className="step-card">
            <div className="step-number">4</div>
            <h3 className="step-title">Retrait ou livraison</h3>
            <p className="step-description">
              Récupérez votre raquette à l'atelier ou faites-vous la livrer directement.
            </p>
          </div>
        </div>

        <p className="reassurance-text">
          ⏱️ Raquette prête en 24 à 48h selon la charge
        </p>
      </section>

      {/* Pricing Section */}
      <section className="pricing-section">
        <h2 className="section-title">Tarifs transparents</h2>
        
        <div className="pricing-grid">
          <div className="pricing-card">
            <div className="pricing-header">
              <h3 className="pricing-title">Avec cordage inclus</h3>
              <div className="pricing-amount">CHF 35–50</div>
            </div>
            <ul className="pricing-features">
              <li>✓ Cordage de qualité</li>
              <li>✓ Main d'œuvre</li>
              <li>✓ Montage soigné</li>
              <li>✓ Contrôle final</li>
              <li>✓ Conseils personnalisés</li>
            </ul>
            <button className="btn-pricing" onClick={handleBooking}>
              Choisir cette option
            </button>
          </div>

          <div className="pricing-card pricing-card-highlight">
            <div className="pricing-badge">Populaire</div>
            <div className="pricing-header">
              <h3 className="pricing-title">Sans cordage</h3>
              <div className="pricing-amount">CHF 20</div>
            </div>
            <ul className="pricing-features">
              <li>✓ Vous fournissez le cordage</li>
              <li>✓ Main d'œuvre</li>
              <li>✓ Montage soigné</li>
              <li>✓ Contrôle final</li>
              <li>✓ Conseils personnalisés</li>
            </ul>
            <button className="btn-pricing btn-pricing-primary" onClick={handleBooking}>
              Choisir cette option
            </button>
          </div>
        </div>

        <p className="pricing-note">
          Service local de qualité à Crissier — Transparence et expertise garanties
        </p>
        <p className="pricing-note" style={{ marginTop: '0.5rem', fontSize: '0.9rem' }}>
          🏅 <strong>Clubs & centres sportifs :</strong> tarifs préférentiels disponibles via notre programme partenaire
        </p>
      </section>

      {/* Expertise Section */}
      <section className="expertise-section">
        <h2 className="section-title">Recommandations selon ton style</h2>
        
        <div className="expertise-grid">
          <div className="expertise-card">
            <h3 className="expertise-title">🎯 Contrôle</h3>
            <p className="expertise-description">
              Cordage multifilament souple avec tension moyenne-haute pour précision maximale et toucher de balle optimal.
            </p>
          </div>

          <div className="expertise-card">
            <h3 className="expertise-title">⚡ Puissance / Confort</h3>
            <p className="expertise-description">
              Cordage synthétique avec tension moyenne-basse pour plus de puissance et confort articulaire.
            </p>
          </div>

          <div className="expertise-card">
            <h3 className="expertise-title">💪 Durabilité</h3>
            <p className="expertise-description">
              Cordage monofilament robuste avec tension adaptée pour résister aux frappes puissantes.
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section">
        <h2 className="section-title">Ce que disent nos clients</h2>
        
        <div className="testimonials-grid">
          {testimonials.map(t => (
            <div key={t.id} className="testimonial-card">
              <div className="testimonial-stars">
                {'★'.repeat(t.rating)}{'☆'.repeat(5 - t.rating)}
              </div>
              <p className="testimonial-text">"{t.comment}"</p>
              <p className="testimonial-author">— {t.reviewer_name}</p>
            </div>
          ))}
        </div>

        <div className="reassurance-badges">
          <div className="reassurance-badge">
            <svg className="reassurance-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span>Service local à Crissier & Lausanne</span>
          </div>
          
          <div className="reassurance-badge">
            <svg className="reassurance-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            <span>Soin du cadre</span>
          </div>
          
          <div className="reassurance-badge">
            <svg className="reassurance-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <span>Conseils personnalisés</span>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <PartnersSection />

      {/* About Section */}
      <section className="about-section">
        <div className="about-content">
          <div className="about-image">
            <div className="about-image-placeholder">
              <svg className="about-image-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
          </div>
          
          <div className="about-text">
            <h2 className="section-title">À propos de StringNow</h2>
            <p className="about-description">
              Service local passionné basé à Crissier, au service de Lausanne et de l'Ouest lausannois. En tant que joueur régulier et membre de club, 
              je comprends l'importance d'un cordage parfaitement réglé pour votre performance.
            </p>
            <p className="about-description">
              Chaque raquette est traitée avec le plus grand soin, sur machine professionnelle, 
              avec des conseils adaptés à votre niveau et style de jeu.
            </p>
            <p className="about-description">
              StringNow collabore avec des clubs de tennis, badminton et squash de la région lausannoise 
              pour offrir à leurs membres des tarifs préférentiels et un service de cordage simplifié. 
              Rejoignez notre réseau de partenaires pour bénéficier d'avantages exclusifs.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <h2 className="section-title">Questions fréquentes</h2>
        
        <div className="faq-grid">
          <details className="faq-item">
            <summary className="faq-question">Quel cordage choisir ?</summary>
            <p className="faq-answer">
              Cela dépend de votre style de jeu. Pour le contrôle, privilégiez un multifilament. 
              Pour la puissance, un synthétique souple. Pour la durabilité, un monofilament. 
              Je vous conseille volontiers selon vos besoins.
            </p>
          </details>

          <details className="faq-item">
            <summary className="faq-question">Quelle tension pour mon niveau ?</summary>
            <p className="faq-answer">
              Débutants : 22-24 kg (plus de confort). Intermédiaires : 24-26 kg (équilibre). 
              Avancés : 26-28 kg (contrôle). Je peux ajuster selon votre ressenti.
            </p>
          </details>

          <details className="faq-item">
            <summary className="faq-question">Puis-je fournir mon cordage ?</summary>
            <p className="faq-answer">
              Oui, absolument ! Le tarif est alors de CHF 25 pour la main d'œuvre. 
              Assurez-vous simplement d'avoir la bonne longueur (minimum 10-12m).
            </p>
          </details>

          <details className="faq-item">
            <summary className="faq-question">Quel est le délai exact ?</summary>
            <p className="faq-answer">
              En moyenne 24 à 48h selon la charge de travail. Pour une urgence, 
              contactez-moi directement, je ferai mon possible pour accélérer.
            </p>
          </details>

          <details className="faq-item">
            <summary className="faq-question">La livraison est-elle possible ?</summary>
            <p className="faq-answer">
              Oui ! Dépôt et retrait à l'atelier à Crissier, ou service d'enlèvement 
              et livraison disponible à Lausanne et dans la région. Tarif selon distance.
            </p>
          </details>

          <details className="faq-item">
            <summary className="faq-question">Pourquoi choisir un service local ?</summary>
            <p className="faq-answer">
              Service personnalisé, conseils d'un joueur passionné, rapidité, 
              soin du cadre et transparence totale. Vous savez exactement qui s'occupe de votre raquette.
            </p>
          </details>

          <details className="faq-item">
            <summary className="faq-question">Proposez-vous des tarifs pour les clubs et centres sportifs ?</summary>
            <p className="faq-answer">
              Oui ! StringNow propose un programme partenaire avec des tarifs préférentiels pour les clubs de tennis, 
              badminton et squash. Les clubs bénéficient d'un espace dédié pour gérer les commandes de leurs membres 
              et de coupons de réduction exclusifs. Contactez-nous pour devenir partenaire.
            </p>
          </details>

          <details className="faq-item">
            <summary className="faq-question">Comment fonctionne l'espace partenaire ?</summary>
            <p className="faq-answer">
              Les clubs partenaires disposent d'un portail dédié pour commander des cordages pour leurs membres, 
              suivre les commandes en cours, gérer les membres du club et profiter de tarifs négociés. 
              L'administrateur du club peut aussi distribuer des coupons de réduction à ses joueurs.
            </p>
          </details>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="final-cta-section">
        <div className="final-cta-content">
          <h2 className="final-cta-title">
            Prêt à jouer avec une raquette parfaitement réglée ?
          </h2>
          <p className="final-cta-subtitle">
            Réserve ton cordage maintenant et profite d'un service rapide, professionnel et personnalisé.
          </p>
          <div className="final-cta-buttons">
            <button className="btn-final-primary" onClick={handleBooking}>
              Réserver un cordage
            </button>
            <button className="btn-final-telegram" onClick={handleContact}>
              <svg className="btn-contact-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
              </svg>
              Contact Telegram
            </button>
            <button className="btn-final-whatsapp" onClick={handleContactWhatsApp}>
              <svg className="btn-contact-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/>
              </svg>
              Contact WhatsApp
            </button>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Home
